import { queryClient } from '../query/client.js';
import { hasUnsavedWork } from './unsaved-work.js';

export const RELOAD_WAIT_TIMEOUT_MS = 10_000;
const POLL_MS = 250;

/**
 * True while an edit is queued for autosave or a write is in flight. Reloading
 * at that moment would either drop the work or trip the `beforeunload` guard's
 * native "Leave site?" dialog.
 */
export function isAppBusy(): boolean {
  return hasUnsavedWork() || queryClient.isMutating() > 0;
}

export type ReloadWhenSafeOptions = {
  reload?: () => void;
  isBusy?: () => boolean;
  timeoutMs?: number;
};

let waiting = false;

/**
 * Reload the page as soon as no save is pending or in flight. Polls `isBusy`
 * and reloads the moment it clears; after `timeoutMs` it reloads regardless —
 * at that point the `beforeunload` guard is the right last resort, because it
 * hands the decision to the user instead of silently dropping work.
 *
 * Concurrent calls collapse into the one already waiting.
 */
export function reloadWhenSafe({
  reload = () => {
    window.location.reload();
  },
  isBusy = isAppBusy,
  timeoutMs = RELOAD_WAIT_TIMEOUT_MS,
}: ReloadWhenSafeOptions = {}): void {
  if (waiting) return;
  if (!isBusy()) {
    reload();
    return;
  }
  waiting = true;
  let elapsed = 0;
  const timer = setInterval(() => {
    elapsed += POLL_MS;
    if (isBusy() && elapsed < timeoutMs) return;
    clearInterval(timer);
    waiting = false;
    reload();
  }, POLL_MS);
}

/** Test-only: forget an in-progress wait so cases don't leak into each other. */
export function resetReloadWhenSafeForTests(): void {
  waiting = false;
}
